import React, {
  FC,
} from 'react';
import {
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import {
  DateRangeFilterProps,
  FilterFieldClassNameRule,
  FilterableSettings,
  FilterComponentsType,
} from 'core/Filters/types';
import Range from 'pages/Dashboard/components/Input/Range';
import {
  validateRange,
} from 'utils/misc';
import {
  MergedFilterValueType,
} from 'core/Filters/attributes';
import MultiSelect from 'core/components/Multiselect';
import LookupWrapper from 'pages/Dashboard/components/LookupWrapper';
import {
  FilterableOptionDTO,
} from 'dtos/filterableOptionDTO';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Controller from 'core/components/FormController';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import MonthPicker, {
  type MonthPickerClasses,
} from 'core/components/DateTimePicker/MonthPicker';
import {
  validateDate,
} from 'pages/Dashboard/utils/helper';
import Select from 'core/components/Select';
import clsx from 'clsx';
import CheckboxInput from 'core/Filters/Components/CheckboxInput';
import isNil from 'lodash/isNil';

const controllerComponentMap: Record<FilterComponentsType, FC<any>> = Object.freeze({
  multiselect: MultiSelect,
  range: Range,
  lookup: LookupWrapper,
  daterange: DateRangePicker,
  date: DatePicker,
  month: MonthPicker,
  year: DatePicker,
  select: Select,
  checkbox: CheckboxInput,
});

type ValidationFunction = Record<
  FilterComponentsType,
  (values: MergedFilterValueType) => boolean
>;

const validationFunctionByType: Partial<ValidationFunction> = {
  range: validateRange,
  date: validateDate,
  month: validateDate,
};

const dateFilterTypes = new Set<FilterComponentsType>([
  'date',
  'month',
  'year',
]);

type Props = {
  components: FilterableSettings[];
  defaultValues?: FieldValues;
  classes?: {
    datePicker?: MonthPickerClasses;
  };
  fieldClassNameRules?: FilterFieldClassNameRule[];
  dateRangeProps?: Record<string, DateRangeFilterProps>;
};

export default function FilterComponents({
  components,
  defaultValues,
  classes,
  fieldClassNameRules,
  dateRangeProps,
}: Props) {
  const { control } = useFormContext<FieldValues>();
  return (
    <>
      {components.map(({
        name,
        filterType,
        displayName,
        optionList,
        lookupTranslator,
        optionLookupUrl,
        isRequired,
        minSearchSize,
        defaultPageSize,
      }: FilterableSettings) => {
        const type = filterType as FilterComponentsType;
        const Component = controllerComponentMap[type];
        let defaultValue: FieldValues[string] | [] = [];
        if (defaultValues) {
          defaultValue = defaultValues[name];
        }
        const isDateFilter = dateFilterTypes.has(type);

        return !isNil(Component) && (
          <Controller
            key={name}
            control={control}
            name={name}
            rules={{
              required: isRequired,
              validate: validationFunctionByType?.[type] ?? (() => true),
            }}
            render={({ field, fieldState: { error, invalid } }) => {
              const componentPropsMap: Partial<
                Record<FilterComponentsType, Record<string, unknown>>
              > = {
                multiselect: {
                  includeAll: true,
                  hasClear: true,
                  error: invalid,
                  getItemKey: (option: FilterableOptionDTO) => option?.value ?? '',
                  getItemLabel: (option: FilterableOptionDTO) => option?.displayName,
                  options: optionList ?? [],
                  placeholder: 'Select',
                  renderOption: (option: FilterableOptionDTO) => option?.displayName,
                  allCaption: 'All',
                },
                select: {
                  error: invalid,
                  getValue: (option: FilterableOptionDTO) => option?.value,
                  getLabel: (option: FilterableOptionDTO) => option?.displayName,
                  options: optionList ?? [],
                  placeholder: 'Select',
                },
                lookup: {
                  invalid,
                  label: displayName ?? '',
                  error,
                  lookupTranslator,
                  optionLookupUrl,
                  minSearchSize,
                  defaultPageSize,
                },
                range: { invalid },
                daterange: {
                  allowEmpty: !isRequired,
                  maxDate: dateRangeProps?.[name]?.maxDate,
                  classes: {
                    root: 'w-full',
                    rangePicker: '!flex-1 !max-w-none',
                  },
                },
                month: { classes: classes?.datePicker },
                checkbox: { displayName: displayName ?? '' },
              };
              let dateProps: Record<string, unknown> = {};
              if (isDateFilter) {
                dateProps = {
                  disableFuture: true,
                  showDateButtons: false,
                  containerClassName: classes?.datePicker?.root,
                  inputContainerClassName: classes?.datePicker?.inputContainer,
                  openOnFocus: type === 'month',
                  inputProps: { readOnly: type === 'month' },
                };
              }

              const fieldClassName = fieldClassNameRules?.find(
                (rule) => rule.type === type && rule.name === name,
              )?.className;

              return (
                <Wrapper
                  id={`filter-${name}`}
                  label={displayName}
                  className={clsx(
                    'w-full !space-y-1',
                    fieldClassName,
                  )}
                  labelClasses={clsx('text-xs', { '!hidden': type === 'checkbox' })}
                >
                  <Component
                    {...field}
                    id={name}
                    defaultValue={defaultValue}
                    className={type === 'daterange' ? undefined : 'flex-1'}
                    {...componentPropsMap[type]}
                    {...dateProps}
                  />
                </Wrapper>
              );
            }}
          />
        );
      })}
    </>
  );
}
